import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { tenantProfileService } from "../services/tenantProfileService";
import { tenantProfileDisplayId } from "./TenantProfileCommonStore";
import { TenantProfile } from "../../../api/graphql";
import { publisherDisplayId } from "../../publisher/store/PublisherCommonStore";
import { countryDisplayId } from "../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../provinceState/store/ProvinceStateCommonStore";
import { vatStatusDisplayId } from "../../vatStatus/store/VatStatusCommonStore";
import { grossReceiptsStatusDisplayId } from "../../grossReceiptsStatus/store/GrossReceiptsStatusCommonStore";
import { currencyDisplayId } from "../../currency/store/CurrencyCommonStore";
import { PersonType } from "../../../api/graphql";
import { publisherService } from "../../publisher/services/publisherService";
import { countryService } from "../../country/services/countryService";
import { provinceStateService } from "../../provinceState/services/provinceStateService";
import { vatStatusService } from "../../vatStatus/services/vatStatusService";
import { grossReceiptsStatusService } from "../../grossReceiptsStatus/services/grossReceiptsStatusService";
import { currencyService } from "../../currency/services/currencyService";
import { InitializeParams } from "../../../store/types";

export class TenantProfileViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = tenantProfileService;
  tenantProfileInstance: TenantProfile | null = null;
  cardState: Record<string, boolean> = {
    business: true,
    address: true,
    billing: true,
    printer: true,
    integrationsSadaic: true,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  personTypeValue: PersonType | "" = "" as PersonType | "";
  legalNameValue: string = "";
  ownPublisherService = publisherService;
  ownPublisherSearchValue: string = "";
  ownPublisherIdValue: string = "";
  ownPublisherSelectedValue: { displayId: string; id: string } | null = null;
  businessIdValue: string = "";
  legalEmailValue: string = "";
  streetAndNumberValue: string = "";
  floorValue: string = "";
  suiteValue: string = "";
  postalCodeValue: string = "";
  countryService = countryService;
  countrySearchValue: string = "";
  countryIdValue: string = "";
  countrySelectedValue: { displayId: string; id: string } | null = null;
  provinceService = provinceStateService;
  provinceSearchValue: string = "";
  provinceIdValue: string = "";
  provinceSelectedValue: { displayId: string; id: string } | null = null;
  cityValue: string = "";
  vatStatusService = vatStatusService;
  vatStatusSearchValue: string = "";
  vatStatusIdValue: string = "";
  vatStatusSelectedValue: { displayId: string; id: string } | null = null;
  taxIdValue: string = "";
  grossReceiptsStatusService = grossReceiptsStatusService;
  grossReceiptsStatusSearchValue: string = "";
  grossReceiptsStatusIdValue: string = "";
  grossReceiptsStatusSelectedValue: { displayId: string; id: string } | null =
    null;
  grossReceiptsIdValue: string = "";
  billingEmailValue: string = "";
  billingCurrencyService = currencyService;
  billingCurrencySearchValue: string = "";
  billingCurrencyIdValue: string = "";
  billingCurrencySelectedValue: { displayId: string; id: string } | null = null;
  printerNameValue: string = "";
  printerStreetAndNumberValue: string = "";
  printerFloorValue: string = "";
  printerSuiteValue: string = "";
  printerPostalCodeValue: string = "";
  printerCountryService = countryService;
  printerCountrySearchValue: string = "";
  printerCountryIdValue: string = "";
  printerCountrySelectedValue: { displayId: string; id: string } | null = null;
  printerProvinceService = provinceStateService;
  printerProvinceSearchValue: string = "";
  printerProvinceIdValue: string = "";
  printerProvinceSelectedValue: { displayId: string; id: string } | null = null;
  printerCityValue: string = "";
  sadaicFtpUrlValue: string = "";
  sadaicFtpUserValue: string = "";
  sadaicEmailValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadTenantProfileById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantProfileOne({ id });
    this.tenantProfileInstance = result.tenantProfileOne;
    if (this.tenantProfileInstance) {
      this.loadValues(this.tenantProfileInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.tenantProfileInstance) {
      return tenantProfileDisplayId(this.tenantProfileInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantProfileSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "ownPublisher") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.publisherOne({
          id: params.returnData.resultId,
        });
        const publisher = result.publisherOne;
        this.ownPublisherSelectedValue = publisher;
      } else if (params.callerData?.fieldName === "country") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.countryOne({ id: params.returnData.resultId });
        const country = result.countryOne;
        this.countrySelectedValue = country;
      } else if (params.callerData?.fieldName === "province") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.provinceStateOne({
          id: params.returnData.resultId,
        });
        const provinceState = result.provinceStateOne;
        this.provinceSelectedValue = provinceState;
      } else if (params.callerData?.fieldName === "vatStatus") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.vatStatusOne({
          id: params.returnData.resultId,
        });
        const vatStatus = result.vatStatusOne;
        this.vatStatusSelectedValue = vatStatus;
      } else if (params.callerData?.fieldName === "grossReceiptsStatus") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.grossReceiptsStatusOne({
          id: params.returnData.resultId,
        });
        const grossReceiptsStatus = result.grossReceiptsStatusOne;
        this.grossReceiptsStatusSelectedValue = grossReceiptsStatus;
      } else if (params.callerData?.fieldName === "billingCurrency") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.currencyOne({
          id: params.returnData.resultId,
        });
        const currency = result.currencyOne;
        this.billingCurrencySelectedValue = currency;
      } else if (params.callerData?.fieldName === "printerCountry") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.countryOne({ id: params.returnData.resultId });
        const country = result.countryOne;
        this.printerCountrySelectedValue = country;
      } else if (params.callerData?.fieldName === "printerProvince") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.provinceStateOne({
          id: params.returnData.resultId,
        });
        const provinceState = result.provinceStateOne;
        this.printerProvinceSelectedValue = provinceState;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getPersonTypeOptions() {
    return this.langStore.PersonTypeOptions;
  }

  get ownPublisherValue() {
    if (this.ownPublisherSelectedValue) {
      return {
        id: this.ownPublisherSelectedValue.id,
        displayId: publisherDisplayId(
          this.ownPublisherSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get countryValue() {
    if (this.countrySelectedValue) {
      return {
        id: this.countrySelectedValue.id,
        displayId: countryDisplayId(this.countrySelectedValue, this.langStore),
      };
    }
    return null;
  }

  get provinceValue() {
    if (this.provinceSelectedValue) {
      return {
        id: this.provinceSelectedValue.id,
        displayId: provinceStateDisplayId(
          this.provinceSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get vatStatusValue() {
    if (this.vatStatusSelectedValue) {
      return {
        id: this.vatStatusSelectedValue.id,
        displayId: vatStatusDisplayId(
          this.vatStatusSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get grossReceiptsStatusValue() {
    if (this.grossReceiptsStatusSelectedValue) {
      return {
        id: this.grossReceiptsStatusSelectedValue.id,
        displayId: grossReceiptsStatusDisplayId(
          this.grossReceiptsStatusSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get billingCurrencyValue() {
    if (this.billingCurrencySelectedValue) {
      return {
        id: this.billingCurrencySelectedValue.id,
        displayId: currencyDisplayId(
          this.billingCurrencySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get printerCountryValue() {
    if (this.printerCountrySelectedValue) {
      return {
        id: this.printerCountrySelectedValue.id,
        displayId: countryDisplayId(
          this.printerCountrySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get printerProvinceValue() {
    if (this.printerProvinceSelectedValue) {
      return {
        id: this.printerProvinceSelectedValue.id,
        displayId: provinceStateDisplayId(
          this.printerProvinceSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.tenantProfileInstance = null;
    this.personTypeValue = "" as PersonType | "";
    this.legalNameValue = "";
    this.ownPublisherSearchValue = "";
    this.ownPublisherIdValue = "";
    this.businessIdValue = "";
    this.legalEmailValue = "";
    this.streetAndNumberValue = "";
    this.floorValue = "";
    this.suiteValue = "";
    this.postalCodeValue = "";
    this.countrySearchValue = "";
    this.countryIdValue = "";
    this.provinceSearchValue = "";
    this.provinceIdValue = "";
    this.cityValue = "";
    this.vatStatusSearchValue = "";
    this.vatStatusIdValue = "";
    this.taxIdValue = "";
    this.grossReceiptsStatusSearchValue = "";
    this.grossReceiptsStatusIdValue = "";
    this.grossReceiptsIdValue = "";
    this.billingEmailValue = "";
    this.billingCurrencySearchValue = "";
    this.billingCurrencyIdValue = "";
    this.printerNameValue = "";
    this.printerStreetAndNumberValue = "";
    this.printerFloorValue = "";
    this.printerSuiteValue = "";
    this.printerPostalCodeValue = "";
    this.printerCountrySearchValue = "";
    this.printerCountryIdValue = "";
    this.printerProvinceSearchValue = "";
    this.printerProvinceIdValue = "";
    this.printerCityValue = "";
    this.sadaicFtpUrlValue = "";
    this.sadaicFtpUserValue = "";
    this.sadaicEmailValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelTenantProfile() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("tenantProfile_update");
  }

  editTenantProfile() {
    const id = this.tenantProfileInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/tenantProfile/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printTenantProfile(handlePrint: () => void): void {
    handlePrint();
  }

  *closeTenantProfile(): any {
    yield this.close();
  }

  loadValues(instance: TenantProfile) {
    this.idValue = instance.id;
    this.personTypeValue = instance.personType || ("" as PersonType | "");
    this.legalNameValue = instance.legalName || "";
    this.ownPublisherSelectedValue = instance.ownPublisher
      ? instance.ownPublisher
      : null;
    this.businessIdValue = instance.businessId || "";
    this.legalEmailValue = instance.legalEmail || "";
    this.streetAndNumberValue = instance.streetAndNumber || "";
    this.floorValue = instance.floor || "";
    this.suiteValue = instance.suite || "";
    this.postalCodeValue = instance.postalCode || "";
    this.countrySelectedValue = instance.country ? instance.country : null;
    this.provinceSelectedValue = instance.province ? instance.province : null;
    this.cityValue = instance.city || "";
    this.vatStatusSelectedValue = instance.vatStatus
      ? instance.vatStatus
      : null;
    this.taxIdValue = instance.taxId || "";
    this.grossReceiptsStatusSelectedValue = instance.grossReceiptsStatus
      ? instance.grossReceiptsStatus
      : null;
    this.grossReceiptsIdValue = instance.grossReceiptsId || "";
    this.billingEmailValue = instance.billingEmail || "";
    this.billingCurrencySelectedValue = instance.billingCurrency
      ? instance.billingCurrency
      : null;
    this.printerNameValue = instance.printerName || "";
    this.printerStreetAndNumberValue = instance.printerStreetAndNumber || "";
    this.printerFloorValue = instance.printerFloor || "";
    this.printerSuiteValue = instance.printerSuite || "";
    this.printerPostalCodeValue = instance.printerPostalCode || "";
    this.printerCountrySelectedValue = instance.printerCountry
      ? instance.printerCountry
      : null;
    this.printerProvinceSelectedValue = instance.printerProvince
      ? instance.printerProvince
      : null;
    this.printerCityValue = instance.printerCity || "";
    this.sadaicFtpUrlValue = instance.sadaicFtpUrl || "";
    this.sadaicFtpUserValue = instance.sadaicFtpUser || "";
    this.sadaicEmailValue = instance.sadaicEmail || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadTenantProfileById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
